import { useState, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import {
  caseStudies,
  experiments,
  showcaseProjects,
} from "../data/caseStudies";
import CaseStudyDrawer from "./CaseStudyDrawer";
import ShowcaseDrawer from "./ShowcaseDrawer";

gsap.registerPlugin(ScrollTrigger);

/* Apple palette per project */
const PAL = {
  "culture-in-your-city": {
    accent: "#0A84FF",
    cardBg: "rgba(10,132,255,0.038)",
    border: "rgba(10,132,255,0.15)",
    pillBg: "rgba(10,132,255,0.09)",
    pillText: "#0055CC",
    stripFrom: "#0A84FF",
    stripTo: "#38A8FF",
    orb: "rgba(10,132,255,0.12)",
  },
  sagenest: {
    accent: "#34C759",
    cardBg: "rgba(52,199,89,0.038)",
    border: "rgba(52,199,89,0.16)",
    pillBg: "rgba(52,199,89,0.09)",
    pillText: "#1A7A35",
    stripFrom: "#34C759",
    stripTo: "#5EE07A",
    orb: "rgba(52,199,89,0.12)",
  },
  deskhero: {
    accent: "#FF9500",
    cardBg: "rgba(255,149,0,0.038)",
    border: "rgba(255,149,0,0.16)",
    pillBg: "rgba(255,149,0,0.09)",
    pillText: "#884F00",
    stripFrom: "#FF9500",
    stripTo: "#FFB340",
    orb: "rgba(255,149,0,0.12)",
  },
};

const HOVER_IMG = {
  "culture-in-your-city": "/img/culturecity_hover.png",
  sagenest: "/img/sagenest_hover.png",
  deskhero: "/img/deskhero_hover.png",
};

const SHOWCASE_IDS = ["aethercore", "iphone"];

/* ── Status badge ── */
const StatusBadge = ({ status }) => {
  const isLive = status === "Live";
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 font-robert-medium text-[8.5px] uppercase"
      style={{
        background: isLive ? "rgba(52,199,89,0.11)" : "rgba(10,132,255,0.09)",
        color: isLive ? "#1A7A35" : "#004ECC",
        letterSpacing: "0.22em",
        border: isLive
          ? "1px solid rgba(52,199,89,0.2)"
          : "1px solid rgba(10,132,255,0.16)",
      }}
    >
      {isLive && (
        <span
          style={{
            display: "inline-block",
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: "#34C759",
            animation: "pulse-live 1.6s ease-in-out infinite",
            flexShrink: 0,
          }}
        />
      )}
      {status}
    </span>
  );
};

/* ── Project card ── */
const ProjectCard = ({
  study,
  animCls,
  gridStyle = {},
  onClick,
  wide = false,
}) => {
  const p = PAL[study.id] ?? PAL["culture-in-your-city"];
  const hoverImg = HOVER_IMG[study.id] ?? null;
  const index = caseStudies.findIndex((s) => s.id === study.id);

  return (
    <div
      className={`${animCls} group relative flex cursor-pointer flex-col overflow-hidden rounded-[20px] ${wide ? "min-h-[210px]" : "min-h-[175px]"} md:min-h-0`}
      style={{
        background: "#FFFFFF",
        border: `1px solid ${p.border}`,
        boxShadow: "0 2px 4px rgba(0,0,0,0.3), 0 8px 32px rgba(0,0,0,0.28)",
        ...gridStyle,
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      aria-label={`Open ${study.title} case study`}
    >
      {/* Accent strip */}
      <div
        style={{
          height: 3,
          background: `linear-gradient(90deg, ${p.stripFrom}, ${p.stripTo})`,
          flexShrink: 0,
        }}
      />

      {/* Card body — justify-between pushes top content and bottom pills apart intentionally */}
      <div
        className="relative flex flex-1 flex-col justify-between overflow-hidden p-5 sm:p-6"
        style={{ background: p.cardBg, minHeight: 0 }}
      >
        {/* ── Ambient orb (top-right corner glow) ── */}
        <div
          className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full"
          style={{
            background: `radial-gradient(circle, ${p.orb} 0%, transparent 70%)`,
            filter: "blur(24px)",
          }}
          aria-hidden="true"
        />

        {/* ── Large watermark index number ── */}
        <div
          className="pointer-events-none absolute bottom-3 right-4 select-none font-robert-medium font-bold leading-none"
          style={{
            fontSize: wide
              ? "clamp(5rem, 9vw, 10rem)"
              : "clamp(4rem, 7vw, 7.5rem)",
            color: p.accent,
            opacity: 0.055,
            letterSpacing: "-0.04em",
          }}
          aria-hidden="true"
        >
          {String(index + 1).padStart(2, "0")}
        </div>

        {/* ── TOP CONTENT GROUP ── */}
        <div className="relative z-10">
          {/* Role / context / badge row */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <p
                className="font-robert-medium uppercase"
                style={{
                  fontSize: "8px",
                  letterSpacing: "0.28em",
                  color: p.accent,
                }}
              >
                {study.role}
              </p>
              <p
                className="mt-0.5 font-robert-medium"
                style={{
                  fontSize: "7.5px",
                  letterSpacing: "0.14em",
                  color: "#A0A0A8",
                }}
              >
                {study.context}
              </p>
            </div>
            <StatusBadge status={study.status} />
          </div>

          {/* Title — dominant, larger on wide card */}
          <h3
            className="mt-3 font-robert-medium font-bold leading-[1.08] text-[#1C1C1E]"
            style={{
              fontSize: wide
                ? "clamp(1.5rem, 2.8vw, 2.7rem)"
                : "clamp(1.05rem, 1.75vw, 1.6rem)",
            }}
          >
            {study.title}
          </h3>

          {/* Description */}
          <p
            className="mt-2.5 leading-relaxed text-[#4A4A50]"
            style={{
              fontSize: "clamp(0.67rem, 0.9vw, 0.79rem)",
              fontFamily: "var(--font-robert-regular)",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: wide ? 2 : 3,
              WebkitBoxOrient: "vertical",
            }}
          >
            {study.description}
          </p>

          {/* Key insight quote — wide card only */}
          {wide && study.approach?.[0] && (
            <div
              className="mt-4 rounded-[10px] px-4 py-3"
              style={{
                background: `${p.accent}0C`,
                borderLeft: `2px solid ${p.accent}55`,
              }}
            >
              <p
                className="font-robert-medium leading-snug text-[#3A3A3C]"
                style={{
                  fontSize: "clamp(0.62rem, 0.82vw, 0.72rem)",
                  fontStyle: "italic",
                }}
              >
                "{study.approach[0]}"
              </p>
            </div>
          )}
        </div>

        {/* ── BOTTOM GROUP — anchored to bottom ── */}
        <div className="relative z-10 mt-4 flex flex-wrap gap-1.5">
          {study.stack.map((tech) => (
            <span
              key={tech}
              className="rounded-full font-robert-medium text-[8px] font-semibold"
              style={{
                background: p.pillBg,
                color: p.pillText,
                padding: "3px 9px",
                letterSpacing: "0.04em",
                border: `1px solid ${p.border}`,
              }}
            >
              {tech}
            </span>
          ))}
        </div>
      </div>

      {/* ── Hover screenshot overlay ── */}
      {hoverImg && (
        <div className="pointer-events-none absolute inset-0 z-30 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <img
            src={hoverImg}
            alt={`${study.title} preview`}
            className="h-full w-full object-cover"
            draggable={false}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.05) 100%)",
            }}
          />
          <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
            <div>
              <h3
                className="font-robert-medium font-bold leading-tight text-white"
                style={{ fontSize: "clamp(1.1rem, 2.2vw, 2rem)" }}
              >
                {study.title}
              </h3>
              <p
                className="mt-1 font-robert-medium text-white/50"
                style={{
                  fontSize: "8px",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                }}
              >
                {study.context}
              </p>
            </div>
            <StatusBadge status={study.status} />
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Showcase column (2 stacked mini-cards replacing stats) ── */
const ShowcaseColumn = ({ animCls, onOpen }) => {
  const SHOWCASE = [
    {
      id: "aethercore",
      title: "AetherCore",
      description:
        "Cinematic gaming AI landing page with eye-catching animations & immersive scroll effects.",
      label: "Gaming AI",
      accent: "#7C3AED",
      bg: "rgba(124,58,237,0.05)",
      border: "rgba(124,58,237,0.18)",
      strip: ["#7C3AED", "#A855F7"],
      hoverImg: "/img/aethercore_hover.png",
    },
    {
      id: "iphone",
      title: "3D iPhone Showcase",
      description:
        "Interactive iPhone 15 experience built with Three.js, GSAP & WebGL rendering.",
      label: "Three.js · WebGL",
      accent: "#3A3A3C",
      bg: "rgba(28,28,30,0.035)",
      border: "rgba(28,28,30,0.12)",
      strip: ["#3A3A3C", "#6A6A6E"],
      hoverImg: "/img/iphone_hover.png",
    },
  ];

  return (
    <div
      className={`${animCls} flex flex-col gap-[10px] min-h-[240px] md:min-h-0`}
      style={{ height: "100%" }}
    >
      {SHOWCASE.map((proj) => (
        <div
          key={proj.id}
          onClick={() => {
            const data = showcaseProjects.find((s) => s.id === proj.id);
            if (data) onOpen(data);
          }}
          className="group relative flex flex-1 flex-col overflow-hidden rounded-[20px]"
          style={{
            background: "#FFFFFF",
            border: `1px solid ${proj.border}`,
            boxShadow: "0 2px 4px rgba(0,0,0,0.3), 0 8px 28px rgba(0,0,0,0.24)",
            cursor: "pointer",
            minHeight: 0,
          }}
        >
          {/* Accent strip */}
          <div
            style={{
              height: 3,
              background: `linear-gradient(90deg, ${proj.strip[0]}, ${proj.strip[1]})`,
              flexShrink: 0,
            }}
          />

          {/* Body */}
          <div
            className="relative flex flex-1 flex-col justify-between p-4 sm:p-5"
            style={{ background: proj.bg, minHeight: 0 }}
          >
            {/* Ambient orb */}
            <div
              className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full"
              style={{
                background: `radial-gradient(circle, ${proj.accent}18 0%, transparent 70%)`,
                filter: "blur(16px)",
              }}
              aria-hidden="true"
            />

            <div className="relative z-10">
              <p
                className="font-robert-medium uppercase"
                style={{
                  fontSize: "7.5px",
                  letterSpacing: "0.28em",
                  color: proj.accent,
                }}
              >
                {proj.label} · Experiment
              </p>
              <p
                className="mt-2 font-robert-medium font-bold leading-[1.15] text-[#1C1C1E]"
                style={{ fontSize: "clamp(0.88rem, 1.4vw, 1.15rem)" }}
              >
                {proj.title}
              </p>
              <p
                className="mt-1.5 leading-snug text-[#6E6E73]"
                style={{
                  fontSize: "clamp(0.62rem, 0.8vw, 0.71rem)",
                  fontFamily: "var(--font-robert-regular)",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {proj.description}
              </p>
            </div>

            <p
              className="relative z-10 font-robert-medium"
              style={{
                fontSize: "8px",
                letterSpacing: "0.18em",
                color: "#A0A0A8",
                textTransform: "uppercase",
              }}
            >
              Click to explore
            </p>
          </div>

          {/* Hover screenshot overlay */}
          {proj.hoverImg && (
            <div className="pointer-events-none absolute inset-0 z-30 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <img
                src={proj.hoverImg}
                alt={`${proj.title} preview`}
                className="h-full w-full object-cover"
                draggable={false}
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.2) 55%, rgba(0,0,0,0.05) 100%)",
                }}
              />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-2">
                <p
                  className="font-robert-medium font-bold leading-tight text-white"
                  style={{ fontSize: "clamp(0.85rem, 1.3vw, 1.1rem)" }}
                >
                  {proj.title}
                </p>
                <p
                  className="shrink-0 font-robert-medium uppercase text-white/60"
                  style={{ fontSize: "7.5px", letterSpacing: "0.22em" }}
                >
                  {proj.label}
                </p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ── Single experiment tile with hover state ── */
const ExpTile = ({ exp, color }) => {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={exp.link}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        borderRadius: "10px",
        padding: "0 14px",
        textDecoration: "none",
        background: hov ? `${color}20` : `${color}12`,
        border: `1px solid ${hov ? `${color}50` : `${color}28`}`,
        boxShadow: hov
          ? `0 8px 24px ${color}22, inset 0 1px 0 rgba(255,255,255,0.5)`
          : "inset 0 1px 0 rgba(255,255,255,0.3)",
        transform: hov
          ? "translateY(-2.5px) scale(1.022)"
          : "translateY(0) scale(1)",
        transition:
          "transform 0.32s cubic-bezier(0.34,1.56,0.64,1), background 0.22s ease, border-color 0.22s ease, box-shadow 0.25s ease",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-robert-medium)",
          fontSize: "clamp(0.65rem, 0.84vw, 0.74rem)",
          fontWeight: 600,
          letterSpacing: "0.01em",
          color: hov ? color : "#2A2A2E",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          transition: "color 0.22s ease",
        }}
      >
        {exp.title}
      </span>
      <svg
        width="9"
        height="9"
        viewBox="0 0 12 12"
        fill="none"
        style={{
          flexShrink: 0,
          opacity: hov ? 1 : 0.28,
          transform: hov ? "translate(2px, -2px)" : "translate(0, 0)",
          transition:
            "opacity 0.22s ease, transform 0.32s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        <path
          d="M2.5 9.5L9.5 2.5M9.5 2.5H4M9.5 2.5V8"
          stroke={color}
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </a>
  );
};

/* ── Experiments card ── */
const ExperimentsCard = ({ animCls, gridStyle = {} }) => {
  const shown = experiments.filter((e) => !SHOWCASE_IDS.includes(e.id));

  const TILE_COLORS = [
    "#0A84FF",
    "#34C759",
    "#FF9500",
    "#0A84FF",
    "#34C759",
    "#FF9500",
  ];

  return (
    <div
      className={`${animCls} relative flex flex-col overflow-hidden rounded-[20px] min-h-[210px] md:min-h-0`}
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 2px 4px rgba(0,0,0,0.3), 0 8px 28px rgba(0,0,0,0.24)",
        ...gridStyle,
      }}
    >
      {/* Multi-color accent strip */}
      <div
        style={{
          height: 3,
          background:
            "linear-gradient(90deg, #0A84FF 0%, #0A84FF 33%, #34C759 33%, #34C759 66%, #FF9500 66%, #FF9500 100%)",
          flexShrink: 0,
        }}
      />

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <p
            className="font-robert-medium uppercase"
            style={{
              fontSize: "8px",
              letterSpacing: "0.3em",
              color: "#8E8E93",
            }}
          >
            Side Projects
          </p>
          <p
            className="font-robert-medium"
            style={{
              fontSize: "8px",
              color: "#C0C0C8",
              letterSpacing: "0.1em",
            }}
          >
            {shown.length} projects
          </p>
        </div>

        <div
          className="flex-1"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "1fr 1fr 1fr",
            gap: "8px",
          }}
        >
          {shown.map((exp, i) => (
            <ExpTile
              key={exp.id}
              exp={exp}
              color={TILE_COLORS[i % TILE_COLORS.length]}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── GitHub CTA card ── */
const CTACard = ({ animCls }) => (
  <a
    href="https://github.com/Vishvjeet-Rana"
    target="_blank"
    rel="noopener noreferrer"
    className={`${animCls} group relative flex flex-col justify-between overflow-hidden rounded-[20px] p-5 sm:p-6`}
    style={{
      background: "linear-gradient(145deg, #0A84FF 0%, #0066CC 100%)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow:
        "0 4px 32px rgba(10,132,255,0.45), inset 0 1px 0 rgba(255,255,255,0.15)",
      textDecoration: "none",
    }}
  >
    {/* Shine orb */}
    <div
      className="pointer-events-none absolute -right-8 -top-8 h-36 w-36 rounded-full"
      style={{
        background:
          "radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)",
        filter: "blur(16px)",
      }}
      aria-hidden="true"
    />

    <p
      className="relative z-10 font-robert-medium font-bold uppercase text-white"
      style={{
        fontSize: "clamp(0.6rem, 2.2vw, 0.6rem)",
        letterSpacing: "0.04em",
        lineHeight: 1,
      }}
    >
      Source Code
    </p>

    <div className="relative z-10">
      <p
        className="font-robert-medium font-bold leading-[1.15] text-white"
        style={{ fontSize: "clamp(0.95rem, 1.7vw, 1.5rem)" }}
      >
        All projects on GitHub →
      </p>
      <p
        className="mt-2 font-robert-medium font-bold text-white"
        style={{
          fontSize: "clamp(0.9rem, 1.6vw, 0.9rem)",
          letterSpacing: "0.01em",
          lineHeight: 1,
        }}
      >
        @Vishvjeet-Rana
      </p>
    </div>

    <div
      className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 group-hover:scale-110"
      style={{
        background: "rgba(255,255,255,0.18)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(255,255,255,0.2)",
      }}
    >
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
        <path
          d="M2.5 9.5L9.5 2.5M9.5 2.5H4M9.5 2.5V8"
          stroke="white"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  </a>
);

/* ── Section ── */
const Evidence = () => {
  const sectionRef = useRef(null);
  const [activeStudy, setActiveStudy] = useState(null);
  const [activeShowcase, setActiveShowcase] = useState(null);

  useGSAP(
    () => {
      const pref = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (pref) {
        gsap.set(
          [
            ".ev-hdr",
            ".ev-p1",
            ".ev-p2",
            ".ev-p3",
            ".ev-sc",
            ".ev-e",
            ".ev-cta",
          ],
          { opacity: 1, x: 0, y: 0 },
        );
        return;
      }

      gsap.set(".ev-hdr", { opacity: 0, y: -14 });
      gsap.set(".ev-p1", { x: -60, opacity: 0 });
      gsap.set(".ev-p2", { y: -40, opacity: 0 });
      gsap.set(".ev-p3", { x: 60, opacity: 0 });
      gsap.set(".ev-sc", { y: 44, opacity: 0 });
      gsap.set(".ev-e", { y: 44, opacity: 0 });
      gsap.set(".ev-cta", { y: 44, opacity: 0 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 65%",
          once: true,
        },
      });

      tl.to(".ev-hdr", { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" })
        .to(
          ".ev-p1",
          { x: 0, opacity: 1, duration: 0.85, ease: "expo.out" },
          "-=0.2",
        )
        .to(
          ".ev-p2",
          { y: 0, opacity: 1, duration: 0.85, ease: "expo.out" },
          "-=0.7",
        )
        .to(
          ".ev-p3",
          { x: 0, opacity: 1, duration: 0.85, ease: "expo.out" },
          "-=0.72",
        )
        .to(
          ".ev-sc",
          { y: 0, opacity: 1, duration: 0.7, ease: "power2.out" },
          "-=0.4",
        )
        .to(
          ".ev-e",
          { y: 0, opacity: 1, duration: 0.68, ease: "power2.out" },
          "-=0.58",
        )
        .to(
          ".ev-cta",
          { y: 0, opacity: 1, duration: 0.68, ease: "power2.out" },
          "-=0.6",
        );
    },
    { scope: sectionRef },
  );

  return (
    <>
      {activeStudy && (
        <CaseStudyDrawer
          study={activeStudy}
          onClose={() => setActiveStudy(null)}
        />
      )}
      {activeShowcase && (
        <ShowcaseDrawer
          project={activeShowcase}
          onClose={() => setActiveShowcase(null)}
        />
      )}

      <section
        ref={sectionRef}
        id="evidence"
        className="relative overflow-hidden min-h-[100dvh] md:h-dvh"
        style={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          padding:
            "clamp(20px,3.8vw,48px) clamp(12px,3.2vw,40px) clamp(20px,3.2vw,40px)",
          gap: "14px",
        }}
      >
        {/* ── Ambient animated gradient bg ──
            4 orbs, each a different Apple accent at low opacity.
            Heavy blur + alternate direction = premium aurora, not screensaver.
        ── */}
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden"
          aria-hidden="true"
        >
          {/* Blue — top-left, sweeps half the section */}
          <div
            style={{
              position: "absolute",
              top: "-20%",
              left: "-12%",
              width: "72%",
              height: "75%",
              background:
                "radial-gradient(circle, rgba(10,132,255,0.38) 0%, transparent 65%)",
              filter: "blur(90px)",
              willChange: "transform",
              animation: "ev-blob-1 15s ease-in-out infinite alternate",
            }}
          />
          {/* Orange — bottom-right, sweeps other half */}
          <div
            style={{
              position: "absolute",
              bottom: "-15%",
              right: "-5%",
              width: "65%",
              height: "68%",
              background:
                "radial-gradient(circle, rgba(255,149,0,0.28) 0%, transparent 65%)",
              filter: "blur(80px)",
              willChange: "transform",
              animation: "ev-blob-2 19s ease-in-out infinite alternate",
            }}
          />
          {/* Green — top-right, bridges the two */}
          <div
            style={{
              position: "absolute",
              top: "-5%",
              right: "5%",
              width: "45%",
              height: "50%",
              background:
                "radial-gradient(circle, rgba(52,199,89,0.2) 0%, transparent 65%)",
              filter: "blur(72px)",
              willChange: "transform",
              animation: "ev-blob-3 22s ease-in-out infinite alternate",
            }}
          />
          {/* White — center highlight, keeps middle from going too dark */}
          <div
            style={{
              position: "absolute",
              top: "30%",
              left: "28%",
              width: "44%",
              height: "44%",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)",
              filter: "blur(64px)",
              willChange: "transform",
              animation: "ev-blob-4 17s ease-in-out infinite alternate",
            }}
          />
        </div>

        {/* Header */}
        <div className="ev-hdr flex items-center justify-between">
          <p
            className="font-robert-medium uppercase"
            style={{
              fontSize: "12px",
              letterSpacing: "0.3em",
              color: "#A0A0A8",
            }}
          >
            02 — The Work
          </p>
          <p
            className="font-robert-medium uppercase"
            style={{
              fontSize: "12px",
              letterSpacing: "0.22em",
              color: "#A0A0A8",
            }}
          >
            {caseStudies.length} Case Studies ·{" "}
            {experiments.filter((e) => !SHOWCASE_IDS.includes(e.id)).length}{" "}
            Experiments
          </p>
        </div>

        {/* Bento grid */}
        <div
          className="grid grid-cols-2 [grid-auto-flow:dense] gap-[8px] overflow-hidden md:gap-[10px] md:h-full md:[grid-template-columns:0.9fr_1.1fr_0.9fr_1.1fr] md:[grid-template-rows:1.6fr_1fr] md:[grid-auto-flow:row]"
        >
          {/* ROW 1 */}
          <ProjectCard
            study={caseStudies[0]}
            animCls="ev-p1"
            gridStyle={{ gridColumn: "span 2" }}
            onClick={() => setActiveStudy(caseStudies[0])}
            wide
          />
          <ProjectCard
            study={caseStudies[1]}
            animCls="ev-p2"
            onClick={() => setActiveStudy(caseStudies[1])}
          />
          <ProjectCard
            study={caseStudies[2]}
            animCls="ev-p3"
            onClick={() => setActiveStudy(caseStudies[2])}
          />

          {/* ROW 2 */}
          <ShowcaseColumn animCls="ev-sc" onOpen={setActiveShowcase} />
          <ExperimentsCard
            animCls="ev-e"
            gridStyle={{ gridColumn: "span 2" }}
          />
          <CTACard animCls="ev-cta" />
        </div>
      </section>
    </>
  );
};

export default Evidence;
